import React, { useEffect } from "react";
import { VariantGroup, VariantOption } from "../actions/types";

// ─────────────────────────────────────────────────────────────────────────────
// VariantPicker
// Componente genérico que renderiza un grupo de variantes de producto como pills.
// Reemplaza y generaliza el antiguo SizePicker.
// Para agregar soporte visual especial (ej: swatches de color), extender el
// render por groupType en el futuro.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  group: VariantGroup;
  selectedId: number | string | null;
  onSelect: (id: number | string) => void;
  /** Color activo de los pills (viene de useTenant().primaryColor) */
  activeColor?: string;
  /** Color de texto sobre el pill activo */
  activeTextColor?: string;
  size?: "sm" | "md" | "lg";
}

const STYLE_TAG_ID = "__variant_picker_styles";

const ensureStyleTag = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .variant-picker { display:flex; gap:.5rem; flex-wrap:wrap; }
    .variant-pill { position:relative; outline:none; }
    .variant-pill:not(:disabled):hover { border-color:#000 !important; }
    .variant-pill:focus-visible { box-shadow:0 0 0 2px #0003, 0 0 0 4px var(--vp-active, #000); }
  `;
  document.head.appendChild(style);
};

const SIZE_OVERRIDES: Record<string, React.CSSProperties> = {
  sm: { minWidth: 32, height: 32, fontSize: ".75rem" },
  md: {},
  lg: { minWidth: 48, height: 48, fontSize: "1rem" },
};

const VariantPicker: React.FC<Props> = ({
  group,
  selectedId,
  onSelect,
  activeColor = "#000",
  activeTextColor = "#fff",
  size = "md",
}) => {
  useEffect(() => {
    ensureStyleTag();
  }, []);

  const basePill: React.CSSProperties = {
    border: "1px solid #ccc",
    borderRadius: 9999,
    minWidth: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: ".85rem",
    background: "#fff",
    transition: "background .15s, border-color .15s, color .15s",
    padding: "0 .75rem",
    lineHeight: 1,
    userSelect: "none",
  };

  const activeStyle: React.CSSProperties = {
    background: activeColor,
    color: activeTextColor,
    borderColor: activeColor,
    fontWeight: 500,
  };

  const disabledStyle: React.CSSProperties = {
    opacity: 0.35,
    cursor: "not-allowed",
    textDecoration: "line-through",
  };

  return (
    <div
      className="variant-picker"
      role="radiogroup"
      aria-label={`Elegir ${group.label.toLowerCase()}`}
      style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}
    >
      {group.options.map((opt: VariantOption) => {
        const isActive = String(opt.id) === String(selectedId);
        const style: React.CSSProperties = {
          ...basePill,
          ...SIZE_OVERRIDES[size],
          ...(isActive ? activeStyle : {}),
          ...(opt.disabled ? disabledStyle : {}),
          ["--vp-active" as any]: activeColor,
        };
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={opt.disabled}
            className="variant-pill"
            style={style}
            onClick={() => !opt.disabled && onSelect(opt.id)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default VariantPicker;
